import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User, Users, Crown, Briefcase, MessageCircle, MapPin, Calendar } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { supabase } from '../utils/supabase/client';
import { toast } from '../utils/toast';
import { motion } from 'motion/react';
import { devLog, devWarn } from '../utils/logger';
import { PageShell } from './layout/PageShell';

interface GigInterestsViewProps {
  gigId: string;
  gigTitle: string;
  accessToken: string;
  isVisible?: boolean;
  onViewProfile: (userId: string) => void;
  onStartConversation: (userId: string) => void;
  onBack: () => void;
  /** When true (desktop modal), hide the sub-page header; modal chrome shows back button. */
  hideHeader?: boolean;
}

export function GigInterestsView({
  gigId,
  gigTitle,
  accessToken,
  isVisible = true,
  onViewProfile,
  onStartConversation,
  onBack,
  hideHeader = false,
}: GigInterestsViewProps) {
  const [interests, setInterests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactingUserId, setContactingUserId] = useState<string | null>(null);
  const fetchInterestsRef = useRef<() => void>(() => {});
  const interestsFallbackTimerRef = useRef<number | null>(null);

  // iOS PWA: JS-based sticky sub-page header (same pattern as FavoritesPage)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // iOS PWA: when page becomes visible, scroll .sub-page-content to top (same as FavoritesPage)
  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Refetch when gigId or accessToken changes (e.g. after re-login)
  useEffect(() => {
    if (gigId && accessToken && isVisible) {
      void fetchInterests();
    }
  }, [gigId, accessToken, isVisible]);

  useEffect(() => {
    const handleGigInterestsRefresh = () => {
      if (gigId && accessToken && isVisible) {
        void fetchInterests({ silent: true });
      }
    };

    window.addEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    return () => {
      window.removeEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    };
  }, [gigId, accessToken, isVisible]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (!isVisible || !gigId || !accessToken) return;
      if (document.visibilityState === 'visible') {
        void fetchInterests({ silent: true });
      }
    };

    const handleOnline = () => {
      if (!isVisible || !gigId || !accessToken) return;
      void fetchInterests({ silent: true });
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);
    };
  }, [gigId, accessToken, isVisible]);

  // Realtime: when a talent withdraws (or someone shows interest), refresh the list so employer sees updates
  useEffect(() => {
    if (!gigId || !accessToken || !isVisible) return;

    const stopFallbackPolling = (reason: string) => {
      if (interestsFallbackTimerRef.current === null) return;
      window.clearInterval(interestsFallbackTimerRef.current);
      interestsFallbackTimerRef.current = null;
      devLog('✅ GigInterests realtime recovered - stop fallback polling:', reason);
    };

    const startFallbackPolling = (reason: string) => {
      if (interestsFallbackTimerRef.current !== null) return;
      devWarn('⚠️ GigInterests realtime degraded - start fallback polling:', reason);
      fetchInterestsRef.current();
      interestsFallbackTimerRef.current = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          fetchInterestsRef.current();
        }
      }, 30000);
    };

    const channel = supabase
      .channel(`gig-interests:${gigId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'gig_interests', filter: `gig_id=eq.${gigId}` },
        () => { fetchInterestsRef.current(); }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          stopFallbackPolling('SUBSCRIBED');
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          startFallbackPolling(status);
        }
      });
    return () => {
      stopFallbackPolling('cleanup');
      supabase.removeChannel(channel);
    };
  }, [gigId, accessToken, isVisible]);

  const fetchInterests = async (options?: { silent?: boolean }) => {
    if (!gigId) return;
    const silent = options?.silent === true;

    if (!silent) setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/interests`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (!silent) {
          toast.error(data.error || 'Kunne ikke hente interesserte');
        }
        return;
      }

      const data = await response.json();
      setInterests(data.interests || []);
    } catch (error) {
      if (!silent) {
        toast.error('Kunne ikke hente interesserte');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  fetchInterestsRef.current = () => {
    void fetchInterests({ silent: true });
  };

  const handleStartConversation = async (userId: string) => {
    setContactingUserId(userId);
    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}/contact/${userId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        toast.error(data.error || 'Kunne ikke starte samtale');
        return;
      }

      setInterests((prev) =>
        prev.map((interest) =>
          interest.user?.id === userId ? { ...interest, status: 'contacted' } : interest
        )
      );
      window.dispatchEvent(new Event('talentfunn:gig-interests-refresh'));
      window.dispatchEvent(new Event('talentfunn:gigs-refresh'));
      toast.success('Samtale opprettet! Gå til Meldinger for å fortsette');
      onStartConversation(userId);
    } catch (error) {
      toast.error('Kunne ikke starte samtale');
    } finally {
      setContactingUserId(null);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} min siden`;
    if (diffHours < 24) return `${diffHours} timer siden`;
    if (diffDays < 7) return `${diffDays} dager siden`;
    return date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'contacted':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
            ✓ Kontaktet
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
            Avvist
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
            Venter svar
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
      {!hideHeader && (
        <>
          <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-3 min-w-0">
              <Users className="w-8 h-8 text-purple-500 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold truncate">Interesserte</h2>
                <p className="text-sm text-gray-600 truncate">
                  {loading ? '...' : `${interests.length} ${interests.length === 1 ? 'interessert' : 'interesserte'}`} · {gigTitle}
                </p>
              </div>
            </div>
          </div>
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        </>
      )}

      {/* Content – same structure as FavoritesPage */}
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
              <Users className="w-8 h-8 text-purple-500 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-900">Interesserte</h2>
                <p className="text-sm text-gray-600">
                  {loading ? '...' : `${interests.length} ${interests.length === 1 ? 'interessert' : 'interesserte'}`} · {gigTitle}
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600" />
              <p className="mt-4 text-gray-700">Laster interesserte...</p>
            </div>
          ) : interests.length === 0 ? (
            <div className="bg-white px-8 py-12 text-center xl:rounded-3xl xl:shadow-lg xl:border-2 xl:border-purple-100 xl:p-12">
              <User className="w-16 h-16 xl:w-20 xl:h-20 text-gray-400 mx-auto mb-4 xl:mb-6" />
              <h3 className="text-2xl mb-3 xl:mb-4 text-gray-600 xl:bg-gradient-to-r xl:from-purple-600 xl:to-pink-600 xl:bg-clip-text xl:text-transparent pb-1">
                Ingen interesserte ennå
              </h3>
              <p className="text-gray-600 xl:text-gray-500 mb-8">
                Oppdraget ditt er synlig for alle. Det kan ta litt tid før noen viser interesse.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {interests.map((interest: any) => (
                <motion.div
                  key={interest.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-md p-5 hover:shadow-lg transition-shadow"
                >
                  {/* User info */}
                  <div className="flex items-start gap-4 mb-4">
                    {/* Avatar */}
                    <button
                      onClick={() => onViewProfile(interest.user.id)}
                      className="flex-shrink-0"
                    >
                      {interest.user.profileImage ? (
                        <img
                          src={interest.user.profileImage}
                          alt={interest.user.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-purple-200 hover:border-purple-400 transition-colors"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center border-2 border-purple-200">
                          <span className="text-2xl text-white font-bold">
                            {interest.user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </button>

                    {/* User details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <button
                          onClick={() => onViewProfile(interest.user.id)}
                          className="font-semibold hover:text-purple-600 transition-colors truncate"
                        >
                          {interest.user.name}
                        </button>
                        {interest.user.subscription && ['plus', 'employer_plus'].includes(interest.user.subscription) && (
                          <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                        )}
                      </div>

                      {/* User type and location */}
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          interest.user.userType === 'employer'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {interest.user.userType === 'employer' ? 'Bedrift' : 'Talent'}
                        </span>
                        {interest.user.county && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {interest.user.county}
                          </span>
                        )}
                        {getStatusBadge(interest.status)}
                      </div>

                      {/* Bio */}
                      {interest.user.bio && (
                        <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                          {interest.user.bio}
                        </p>
                      )}

                      {/* Skills */}
                      {interest.user.skills && interest.user.skills.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {interest.user.skills.slice(0, 5).map((skill: string, idx: number) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-xs rounded-lg border border-purple-200"
                            >
                              {skill}
                            </span>
                          ))}
                          {interest.user.skills.length > 5 && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{interest.user.skills.length - 5}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pitch message */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-4 border border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Melding fra kandidaten:</span>
                    </div>
                    <p className="text-gray-800 italic">"{interest.message}"</p>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <Calendar className="w-3 h-3" />
                    <span>Viste interesse {formatTimestamp(interest.createdAt)}</span>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => onViewProfile(interest.user.id)}
                      className="flex-1 px-4 py-2.5 bg-white border-2 border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Se profil
                    </button>
                    {interest.status !== 'contacted' && (
                      <button
                        onClick={() => handleStartConversation(interest.user.id)}
                        disabled={contactingUserId === interest.user.id}
                        className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {contactingUserId === interest.user.id ? (
                          <>
                            <Loader size="sm" className="text-white" />
                            Starter...
                          </>
                        ) : (
                          <>
                            <MessageCircle className="w-4 h-4" />
                            Start samtale
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </PageShell>
      </div>
    </div>
  );
}
